/**
 * In-page collection tabs for the product-list section.
 * Toggles panels + syncs header "View all" / title with the active tab.
 * Inactive panels stay in <template> until first open (no image download / CE upgrade).
 */
class ProductListTabs extends HTMLElement {
  connectedCallback() {
    this.#tabs = Array.from(this.querySelectorAll('[data-tab-trigger]'));
    this.#panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
    this.#header = this.closest('[data-product-list-tabs]')?.querySelector('[data-product-list-header]') ?? null;

    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', this.#onTabClick);
      tab.addEventListener('keydown', this.#onTabKeydown);
    });

    const active = this.#tabs.find(
      (tab) => tab.classList.contains('is-active') || tab.getAttribute('aria-selected') === 'true'
    );
    if (active) {
      requestAnimationFrame(() => this.#centerTab(active));
    }
  }

  disconnectedCallback() {
    this.#tabs.forEach((tab) => {
      tab.removeEventListener('click', this.#onTabClick);
      tab.removeEventListener('keydown', this.#onTabKeydown);
    });
  }

  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {HTMLElement | null} */
  #header = null;

  /**
   * @param {MouseEvent} event
   */
  #onTabClick = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;
    this.#activate(tab);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onTabKeydown = (event) => {
    const current = event.currentTarget;
    if (!(current instanceof HTMLElement)) return;

    const index = this.#tabs.indexOf(current);
    if (index < 0) return;

    let nextIndex = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (index + 1) % this.#tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (index - 1 + this.#tabs.length) % this.#tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.#tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = this.#tabs[nextIndex];
    if (!nextTab) return;
    nextTab.focus();
    this.#activate(nextTab);
  };

  /**
   * @param {HTMLElement} activeTab
   */
  #activate(activeTab) {
    const panelId = activeTab.getAttribute('aria-controls');

    this.#tabs.forEach((tab) => {
      const selected = tab === activeTab;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    /** @type {HTMLElement | null} */
    let activePanel = null;

    this.#panels.forEach((panel) => {
      const match = panel.id === panelId;
      panel.hidden = !match;
      if (match) activePanel = panel;
    });

    this.#syncHeader(activeTab);
    this.#centerTab(activeTab);

    if (activePanel) {
      this.#hydratePanel(activePanel);
      requestAnimationFrame(() => this.#resetPanelScroll(activePanel));
    }
  }

  /**
   * Move inert <template> markup into the live panel once.
   * @param {HTMLElement} panel
   */
  #hydratePanel(panel) {
    if (!panel.hasAttribute('data-tab-lazy')) return;

    const template = panel.querySelector('template[data-tab-template]');
    const slot = panel.querySelector('[data-tab-slot]');
    if (!(template instanceof HTMLTemplateElement) || !(slot instanceof HTMLElement)) {
      panel.removeAttribute('data-tab-lazy');
      return;
    }

    if (slot.dataset.hydrated === 'true') return;

    slot.setAttribute('aria-busy', 'true');
    slot.replaceChildren(template.content.cloneNode(true));
    slot.dataset.hydrated = 'true';
    slot.setAttribute('aria-busy', 'false');
    template.remove();
    panel.removeAttribute('data-tab-lazy');
  }

  /**
   * Reset horizontal scroll after a tab becomes visible.
   * @param {HTMLElement} panel
   */
  #resetPanelScroll(panel) {
    const peek = panel.querySelector('.resource-list--mobile-peek');
    if (peek instanceof HTMLElement) {
      peek.scrollLeft = 0;
    }

    const slideshow = panel.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return;

    const slides = slideshow.querySelector('slideshow-slides');
    if (slides instanceof HTMLElement) {
      slides.scrollLeft = 0;
    }

    if (typeof /** @type {{ select?: Function }} */ (slideshow).select === 'function') {
      try {
        /** @type {{ select: Function }} */ (slideshow).select(0, undefined, { animate: false });
      } catch (_) {
        // no-op
      }
    }
  }

  /**
   * Centers the selected tab in the nav (no free scroll UX).
   * @param {HTMLElement} activeTab
   */
  #centerTab(activeTab) {
    const nav = this.querySelector('.home-category-nav');
    if (!(nav instanceof HTMLElement)) return;

    const item = activeTab.closest('.home-category-nav__item') ?? activeTab;
    const navRect = nav.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const delta = itemRect.left - navRect.left - navRect.width / 2 + itemRect.width / 2;
    const nextLeft = Math.max(0, Math.min(nav.scrollWidth - nav.clientWidth, nav.scrollLeft + delta));

    nav.scrollTo({ left: nextLeft, behavior: 'smooth' });
  }

  /**
   * @param {HTMLElement} activeTab
   */
  #syncHeader(activeTab) {
    if (!this.#header) return;

    const title = activeTab.dataset.collectionTitle?.trim();
    const url = activeTab.dataset.collectionUrl?.trim();

    if (title) {
      const heading = this.#header.querySelector('h1, h2, h3, h4, h5, h6');
      if (heading) heading.textContent = title;
    }

    if (url) {
      this.#header.querySelectorAll('a[href]').forEach((link) => {
        if (link instanceof HTMLAnchorElement) {
          link.href = url;
        }
      });
    }
  }
}

if (!customElements.get('product-list-tabs')) {
  customElements.define('product-list-tabs', ProductListTabs);
}
